import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import teams from "./teams";

const apiIdToLocalId = {};
const apiIdToTeam = {};
teams.forEach((t) => {
  if (t.apiId) {
    apiIdToLocalId[t.apiId] = t.id;
    apiIdToTeam[t.apiId] = t;
  }
});

const ROUND_BONUS = {
  "Round of 32": 10,
  "Round of 16": 20,
  "Quarter-finals": 40,
  "Semi-finals": 80,
  "Final": 150,
};

// Points earned by a team in a single match
function matchPoolPts(goals, isWinner, isDraw, round) {
  const ko = !!ROUND_BONUS[round];
  let pts = 0;
  if (ko) {
    pts += (goals || 0) * 2;
    if (isWinner && ROUND_BONUS[round]) pts += ROUND_BONUS[round];
  } else {
    // Group stage
    pts += (goals || 0) * 1;
    if (isWinner) pts += 3;
    if (isDraw) pts += 1;
  }
  return pts;
}

function Flag({ cc, size = 20 }) {
  if (!cc) return null;
  const h = Math.round(size * 0.75);
  return (
    <img src={"https://flagcdn.com/" + size + "x" + h + "/" + cc.toLowerCase() + ".png"}
      alt="" width={size} height={h}
      style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0, verticalAlign: "middle" }}
      onError={(e) => { e.target.style.display = "none"; }} />
  );
}

function getBallColor(fixture, isHome) {
  if (fixture.status === "NS") return "rgba(255,255,255,0.25)";
  if (fixture.status === "Live") return "#FFD700";
  if (fixture.status !== "FT") return "rgba(255,255,255,0.25)";
  const winner = isHome ? fixture.homeWinner : fixture.awayWinner;
  if (winner === true) return "#00C853";
  if (winner === false) return "#E53935";
  return "#f59e0b";
}

const roundOrder = ["Group Stage - 1","Group Stage - 2","Group Stage - 3","Round of 32","Round of 16","Quarter-finals","Semi-finals","Final"];
const isKnockout = (round) => !!ROUND_BONUS[round];

function Fixtures({ contest, user }) {
  const [fixtures, setFixtures] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState("none");
  const [filterMyTeams, setFilterMyTeams] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openRounds, setOpenRounds] = useState({});

  useEffect(() => { fetchFixtures(); fetchMyEntries(); }, []);

  const fetchFixtures = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "fixtures"));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => new Date(a.date) - new Date(b.date));
    setFixtures(data);
    setLoading(false);
    const grouped = groupFixtures(data);
    const rounds = getSortedRounds(grouped);
    const defaultOpen = {};
    let foundActive = false;
    for (const round of rounds) {
      const hasActive = grouped[round].some((f) => f.status === "Live" || f.status === "NS");
      if (hasActive && !foundActive) { defaultOpen[round] = true; foundActive = true; }
      else { defaultOpen[round] = false; }
    }
    if (!foundActive && rounds.length > 0) defaultOpen[rounds[0]] = true;
    setOpenRounds(defaultOpen);
  };

  const fetchMyEntries = async () => {
    const q = query(collection(db, "entries"), where("contestId", "==", contest.id), where("userId", "==", user.uid));
    const snap = await getDocs(q);
    const entryData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setMyEntries(entryData);
    if (entryData.length > 0) setSelectedEntry(entryData[0].id);
  };

  const getPickedLocalIds = () => {
    if (selectedEntry === "none") return new Set();
    const entry = myEntries.find((e) => e.id === selectedEntry);
    if (!entry) return new Set();
    return new Set(Object.values(entry.picks || {}));
  };

  const pickedLocalIds = getPickedLocalIds();
  const isPickedHome = (f) => { const id = apiIdToLocalId[f.homeTeamId]; return id && pickedLocalIds.has(id); };
  const isPickedAway = (f) => { const id = apiIdToLocalId[f.awayTeamId]; return id && pickedLocalIds.has(id); };

  const groupFixtures = (data) => {
    const groups = {};
    for (const f of data) {
      const round = f.round || "Unknown";
      if (!groups[round]) groups[round] = [];
      groups[round].push(f);
    }
    return groups;
  };

  const getSortedRounds = (grouped) => Object.keys(grouped).sort((a, b) => {
    const ai = roundOrder.indexOf(a), bi = roundOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1; if (bi === -1) return -1;
    return ai - bi;
  });

  const toggleRound = (round) => setOpenRounds((prev) => ({ ...prev, [round]: !prev[round] }));

  const expandAll = () => {
    const next = {};
    getSortedRounds(groupFixtures(fixtures)).forEach((r) => { next[r] = true; });
    setOpenRounds(next);
  };

  const collapseAll = () => {
    const next = {};
    getSortedRounds(groupFixtures(fixtures)).forEach((r) => { next[r] = false; });
    setOpenRounds(next);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
  };

  const grouped = groupFixtures(fixtures);
  const sortedRounds = getSortedRounds(grouped);
  const liveFixtures = fixtures.filter((f) => f.status === "Live");
  const hasEntrySelected = selectedEntry !== "none" && pickedLocalIds.size > 0;
  const allOpen = sortedRounds.length > 0 && sortedRounds.every((r) => openRounds[r]);
  const allClosed = sortedRounds.every((r) => !openRounds[r]);

  const roundLabel = (round) => ({
    "Group Stage - 1": "Group Stage — Matchday 1",
    "Group Stage - 2": "Group Stage — Matchday 2",
    "Group Stage - 3": "Group Stage — Matchday 3",
  }[round] || round);

  const roundSummary = (roundFixtures) => {
    const live = roundFixtures.filter((f) => f.status === "Live").length;
    const done = roundFixtures.filter((f) => f.status === "FT").length;
    const upcoming = roundFixtures.filter((f) => f.status === "NS").length;
    if (live > 0) return { text: live + " LIVE", color: "#E53935" };
    if (upcoming === roundFixtures.length) return { text: "Upcoming", color: "rgba(255,255,255,0.3)" };
    if (done === roundFixtures.length) return { text: "Complete", color: "#00C853" };
    return { text: done + "/" + roundFixtures.length + " played", color: "rgba(255,255,255,0.4)" };
  };

  return (
    <div>
      {/* Header row — all controls on one line */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#FFD700", letterSpacing: 1 }}>FIXTURES</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {myEntries.length > 0 && (
            <>
              <label style={{ fontWeight: 600, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Highlight:</label>
              <select value={selectedEntry} onChange={(e) => { setSelectedEntry(e.target.value); setFilterMyTeams(false); }}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "#1a2d4a", color: "white", fontSize: 13 }}>
                <option value="none">No filter</option>
                {myEntries.map((e) => <option key={e.id} value={e.id}>{e.entryName}</option>)}
              </select>
              {hasEntrySelected && (
                <button onClick={() => setFilterMyTeams(!filterMyTeams)}
                  className={"btn btn-sm " + (filterMyTeams ? "btn-outline" : "btn-ghost")}>
                  ⚽ My Teams Only
                </button>
              )}
            </>
          )}
          <button onClick={allOpen ? collapseAll : expandAll} className="btn btn-ghost btn-sm">
            {allOpen ? "⊖ Collapse All" : "⊕ Expand All"}
          </button>
          <button onClick={fetchFixtures} className="btn btn-ghost btn-sm">↻ Refresh</button>
        </div>
      </div>

      {filterMyTeams && hasEntrySelected && (
        <div style={{ background: "rgba(255,215,0,0.06)", border: "1px solid rgba(255,215,0,0.2)", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: "rgba(255,215,0,0.8)" }}>
          Showing only fixtures involving your picked teams
        </div>
      )}

      {/* Live now */}
      {liveFixtures.length > 0 && (
        <div style={{ marginBottom: 16, background: "rgba(229,57,53,0.1)", border: "1px solid rgba(229,57,53,0.3)", borderRadius: 10, padding: 12 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: "#E53935", letterSpacing: 1, marginBottom: 8 }}>🔴 LIVE NOW</div>
          {liveFixtures.map((f) => <FixtureRow key={f.id} fixture={f} isPickedHome={isPickedHome(f)} isPickedAway={isPickedAway(f)} formatDate={formatDate} />)}
        </div>
      )}

      {loading && <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading fixtures...</p>}
      {!loading && fixtures.length === 0 && <p style={{ color: "rgba(255,255,255,0.3)", textAlign: "center", padding: 32 }}>No fixtures available yet.</p>}

      {sortedRounds.map((round) => {
        const allRoundFixtures = grouped[round];
        const roundFixtures = filterMyTeams && hasEntrySelected
          ? allRoundFixtures.filter((f) => isPickedHome(f) || isPickedAway(f))
          : allRoundFixtures;
        if (filterMyTeams && hasEntrySelected && roundFixtures.length === 0) return null;

        const isOpen = openRounds[round];
        const summary = roundSummary(roundFixtures);

        return (
          <div key={round} style={{ marginBottom: 8, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden", background: "#1a2d4a" }}>
            <div onClick={() => toggleRound(round)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", cursor: "pointer", userSelect: "none", background: isOpen ? "rgba(255,255,255,0.04)" : "transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 1, color: "white" }}>{roundLabel(round)}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: summary.color }}>{summary.text}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>{roundFixtures.length} matches</span>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
              </div>
            </div>
            {isOpen && (
              <div style={{ padding: "4px 8px 8px 8px" }}>
                {roundFixtures.map((f) => (
                  <FixtureRow key={f.id} fixture={f} isPickedHome={isPickedHome(f)} isPickedAway={isPickedAway(f)} formatDate={formatDate} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FixtureRow({ fixture, isPickedHome, isPickedAway, formatDate }) {
  const isLive = fixture.status === "Live";
  const isFT = fixture.status === "FT";
  const homeTeam = apiIdToTeam[fixture.homeTeamId];
  const awayTeam = apiIdToTeam[fixture.awayTeamId];
  const ko = isKnockout(fixture.round);

  const homeResultColor = isPickedHome ? getBallColor(fixture, true) : "rgba(255,255,255,0.75)";
  const awayResultColor = isPickedAway ? getBallColor(fixture, false) : "rgba(255,255,255,0.75)";

  // Per-match pool pts — only shown for picked teams
  const isDraw = isFT && fixture.homeWinner === null && fixture.awayWinner === null;
  const homeMatchPts = isFT && isPickedHome ? matchPoolPts(fixture.homeScore, fixture.homeWinner === true, isDraw, fixture.round) : null;
  const awayMatchPts = isFT && isPickedAway ? matchPoolPts(fixture.awayScore, fixture.awayWinner === true, isDraw, fixture.round) : null;

  return (
    <div style={{
      display: "flex", alignItems: "center", padding: "9px 8px",
      borderRadius: 8, marginBottom: 2,
      background: isLive ? "rgba(229,57,53,0.07)" : "rgba(255,255,255,0.02)",
      borderLeft: isPickedHome || isPickedAway ? "3px solid #FFD700" : "3px solid transparent"
    }}>
      {/* Home team */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isPickedHome && <span style={{ fontSize: 13 }}>⚽</span>}
          <span style={{ fontWeight: isPickedHome ? 700 : 500, fontSize: 13, color: homeResultColor }}>
            {fixture.homeTeam}
          </span>
          {homeTeam && homeTeam.cc && <Flag cc={homeTeam.cc} size={20} />}
        </div>
        {homeMatchPts !== null && (
          <span style={{ fontSize: 10, color: homeMatchPts > 0 ? "#FFD700" : "rgba(255,255,255,0.2)" }}>
            {homeMatchPts > 0 ? "+" + homeMatchPts + " pool pts" : "0 pool pts"}
          </span>
        )}
      </div>

      {/* Score / time */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 90, gap: 2 }}>
        {fixture.status === "NS" ? (
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center" }}>{formatDate(fixture.date)}</span>
        ) : (
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: "white", letterSpacing: 2 }}>
            {fixture.homeScore ?? 0} — {fixture.awayScore ?? 0}
          </span>
        )}
        {isLive && <span style={{ fontSize: 10, fontWeight: 700, color: "#E53935" }}>LIVE {fixture.elapsed ? fixture.elapsed + "'" : ""}</span>}
        {isFT && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>Full Time</span>}
      </div>

      {/* Away team */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {awayTeam && awayTeam.cc && <Flag cc={awayTeam.cc} size={20} />}
          <span style={{ fontWeight: isPickedAway ? 700 : 500, fontSize: 13, color: awayResultColor }}>
            {fixture.awayTeam}
          </span>
          {isPickedAway && <span style={{ fontSize: 13 }}>⚽</span>}
        </div>
        {awayMatchPts !== null && (
          <span style={{ fontSize: 10, color: awayMatchPts > 0 ? "#FFD700" : "rgba(255,255,255,0.2)" }}>
            {awayMatchPts > 0 ? "+" + awayMatchPts + " pool pts" : "0 pool pts"}
          </span>
        )}
      </div>
    </div>
  );
}

export default Fixtures;
